import Box from '@mui/material/Box'
import type { SxProps, Theme } from '@mui/material/styles'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import type { ReactElement } from 'react'

import { KestrixDataTableRankSelectCell } from '../KestrixDataTable/KestrixDataTableRankSelectCell.tsx'
import { colorTokens } from '../tokens/colors.ts'
import type {
  CellLink,
  CellValue,
  KestrixPropertyTableColumn
} from './cell-display.types.ts'
import { propertyTableHeaderTokens } from './property-table-header.tokens.ts'
import type { PropertyTableHeaderAlign } from './property-table-header.types.ts'
import type { PinnedColumnLayout } from './property-table-pinned-columns.ts'
import { buildPinnedCellSx } from './property-table-pinned-columns.ts'
import { renderPropertyTableCellContent } from './render-property-table-cell-content.tsx'

const BODY_CELL_SX: SxProps<Theme> = {
  height: colorTokens.table.body.height,
  maxHeight: colorTokens.table.body.height,
  p: 0,
  pl: propertyTableHeaderTokens.cellPaddingLeft,
  boxSizing: 'border-box',
  overflow: 'visible',
  verticalAlign: 'middle',
  position: 'relative',
  borderLeft: 'none',
  borderRight: `1px solid ${propertyTableHeaderTokens.columnBorder}`
}

const BODY_CELL_CONTENT_SX: SxProps<Theme> = {
  minWidth: 0,
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  pr: propertyTableHeaderTokens.cellPaddingRight
}

const SELECTED_ROW_SURFACE = colorTokens.global.primary[50]

const EMPTY_PINNED_LAYOUT: PinnedColumnLayout = {
  leftOffsets: new Map(),
  lastPinnedColumnKey: null,
  tableMinWidth: undefined
}

export type KestrixPropertyTableRowProps = {
  readonly rowKey: string
  readonly row: Readonly<Record<string, CellValue>>
  readonly columns: readonly KestrixPropertyTableColumn[]
  readonly rank?: number
  readonly selected?: boolean
  readonly onToggleSelected?: () => void
  readonly pinnedLayout?: PinnedColumnLayout
  readonly showPinnedShadow?: boolean
  readonly trailingColumn?: {
    readonly key: string
    readonly width: string | number
  }
  readonly onNavButtonClick?: (
    columnKey: string,
    link: CellLink
  ) => void
  readonly sx?: SxProps<Theme>
}

/**
 * Plan table body row.
 *
 * Renders an optional rank/selection cell followed by one cell per data
 * column, dispatching on `column.display` — see {@link CellDisplay}. Scoped
 * to Plan only: no expand/collapse or `PropertyDetailTabs` nesting.
 */
export const KestrixPropertyTableRow = ({
  rowKey,
  row,
  columns,
  rank,
  selected = false,
  onToggleSelected,
  pinnedLayout = EMPTY_PINNED_LAYOUT,
  showPinnedShadow = false,
  trailingColumn,
  onNavButtonClick,
  sx = {}
}: KestrixPropertyTableRowProps): ReactElement => (
  <TableRow
    data-testid="data-row"
    data-row-key={rowKey}
    data-selected={selected ? 'true' : undefined}
    sx={{
      height: colorTokens.table.body.height,
      ...(selected ? { bgcolor: SELECTED_ROW_SURFACE } : {}),
      ...sx
    }}
  >
    {rank !== undefined && onToggleSelected !== undefined ? (
      <TableCell
        data-column-key="selection"
        sx={[
          BODY_CELL_SX,
          { width: 56, px: 1 },
          buildPinnedCellSx(
            'selection',
            pinnedLayout,
            'body',
            showPinnedShadow
          ),
          selected ? { bgcolor: SELECTED_ROW_SURFACE } : {}
        ]}
      >
        <KestrixDataTableRankSelectCell
          checked={selected}
          rank={rank}
          onToggle={onToggleSelected}
        />
      </TableCell>
    ) : null}
    {columns.map((column) => {
      const align: PropertyTableHeaderAlign = column.align ?? 'left'
      return (
        <TableCell
          key={column.key}
          align={align}
          data-testid={`data-cell-${column.key}`}
          data-column-key={column.key}
          sx={[
            BODY_CELL_SX,
            buildPinnedCellSx(
              column.key,
              pinnedLayout,
              'body',
              showPinnedShadow
            ),
            selected ? { bgcolor: SELECTED_ROW_SURFACE } : {}
          ]}
        >
          <Box sx={BODY_CELL_CONTENT_SX}>
            {renderPropertyTableCellContent(column, row[column.key], {
              onNavButtonClick:
                onNavButtonClick == null
                  ? undefined
                  : (link) => onNavButtonClick(column.key, link)
            })}
          </Box>
        </TableCell>
      )
    })}
    {trailingColumn == null ? null : (
      <TableCell
        data-testid={`data-cell-${trailingColumn.key}`}
        data-column-key={trailingColumn.key}
        sx={[
          BODY_CELL_SX,
          {
            width: trailingColumn.width,
            minWidth: trailingColumn.width,
            maxWidth: trailingColumn.width,
            pl: 0
          },
          selected ? { bgcolor: SELECTED_ROW_SURFACE } : {}
        ]}
      />
    )}
  </TableRow>
)
